/**
 * Generated from syncly-creator-spec/open-api/campaign.openapi.yaml by scripts/openapi-converter.
 * Do not edit this file directly.
 */

import z from 'zod';

import {
  CAMPAIGN_ANALYSIS_DIMENSION,
  CAMPAIGN_ANALYSIS_FAILURE_REASON,
  CAMPAIGN_ANALYSIS_STATUS,
  CAMPAIGN_ANALYSIS_TARGET_METRIC,
  CAMPAIGN_LABEL,
  CAMPAIGN_ITEM_TYPE,
  CAMPAIGN_PLATFORM,
  CAMPAIGN_STATUS,
  CAMPAIGN_POST_SOURCE,
  CAMPAIGN_POST_SORT,
  CAMPAIGN_SORT,
} from './campaign-constants';

export const campaignAnalysisAggregateSchema = z.strictObject({
  primary_average_views: z.number(),
  primary_average_engagement_rate: z.number().nullable(),
  baseline_recent_k_posts_average_views: z.number(),
  baseline_recent_k_posts_average_engagement_rate: z.number().nullable(),
  primary_average_views_ratio_to_baseline: z.number(),
  primary_average_engagement_rate_ratio_to_baseline: z.number().nullable(),
  total_views: z.number().int(),
  total_likes: z.number().int(),
  total_comments: z.number().int(),
  total_shares: z.number().int(),
});

export const campaignAnalysisMetricDefinitionSchema = z.strictObject({
  target_metric: z.string(),
  method_label: z.string(),
  description: z.string(),
});

export const campaignAnalysisAppendixSchema = z.strictObject({
  metric_definition: campaignAnalysisMetricDefinitionSchema,
  filters: z.array(z.string()),
});

export const campaignAnalysisCampaignSnapshotSchema = z.strictObject({
  average_views: z.number(),
  average_engagement_rate: z.number().nullable(),
});

export const campaignAnalysisContentCreationGuideSchema = z.strictObject({
  label: z.string(),
  title: z.string(),
  body: z.string(),
});

export const campaignAnalysisPostDistributionGroupSchema = z.strictObject({
  label: z.string(),
  post_count: z.number().int(),
  proportion: z.number(),
});

export const campaignAnalysisCreatorCompositionSchema = z.strictObject({
  gender_distribution: z.array(campaignAnalysisPostDistributionGroupSchema),
  race_distribution: z.array(campaignAnalysisPostDistributionGroupSchema),
  age_distribution: z.array(campaignAnalysisPostDistributionGroupSchema),
  follower_size_distribution: z.array(
    campaignAnalysisPostDistributionGroupSchema,
  ),
});

export const campaignAnalysisCreatorDiscoveryGuideSchema = z.strictObject({
  title: z.string(),
  prompt: z.string(),
});

export const campaignAnalysisCreatorProfileSchema = z.strictObject({
  gender: z.string().nullable(),
  age: z.string().nullable(),
  race: z.string().nullable(),
  location: z.string().nullable(),
  language: z.string().nullable(),
});

export const campaignAnalysisDatasetSchema = z.strictObject({
  campaign_name: z.string(),
  platform: z.string(),
  analyzed_post_count: z.number().int(),
});

export const campaignAnalysisDimensionSchema = z.enum(
  CAMPAIGN_ANALYSIS_DIMENSION,
);

export const campaignAnalysisKeyInsightCardSchema = z.strictObject({
  heading: z.string(),
  title: z.string(),
});

export const campaignAnalysisNextActionSchema = z.strictObject({
  action: z.string(),
  effect: z.string(),
});

export const campaignAnalysisExecutiveSummarySchema = z.strictObject({
  campaign_snapshot: campaignAnalysisCampaignSnapshotSchema,
  key_insight_cards: z.array(campaignAnalysisKeyInsightCardSchema).optional(),
  next_actions: z.array(campaignAnalysisNextActionSchema).optional(),
});

export const campaignAnalysisFailureReasonSchema = z.enum(
  CAMPAIGN_ANALYSIS_FAILURE_REASON,
);

export const campaignAnalysisRequestedBySchema = z.strictObject({
  email: z.string(),
  profile_color: z.number().int(),
  first_name: z.string(),
  last_name: z.string(),
});

export const campaignAnalysisStatusSchema = z.enum(CAMPAIGN_ANALYSIS_STATUS);

export const campaignAnalysisTargetMetricSchema = z.enum(
  CAMPAIGN_ANALYSIS_TARGET_METRIC,
);

export const campaignAnalysisHistoryItemSchema = z.strictObject({
  run_id: z.string(),
  title: z.string().nullable().optional(),
  dimensions: z.array(campaignAnalysisDimensionSchema),
  target_metric: campaignAnalysisTargetMetricSchema,
  status: campaignAnalysisStatusSchema,
  requested_by: campaignAnalysisRequestedBySchema,
  failure_reason: campaignAnalysisFailureReasonSchema.nullable().optional(),
  post_count: z.number().int().nullable().optional(),
  created_at: z.iso.datetime({ offset: true }),
  started_at: z.iso.datetime({ offset: true }).nullable().optional(),
  completed_at: z.iso.datetime({ offset: true }).nullable().optional(),
});

export const campaignAnalysisHistoryResponseSchema = z.strictObject({
  items: z.array(campaignAnalysisHistoryItemSchema).max(20),
  next_cursor: z.string().nullable().optional(),
});

export const campaignAnalysisRepresentativePostExampleSchema = z.strictObject({
  views: z.number().int(),
  engagement_rate: z.number().nullable(),
  post_source_url: z.string(),
  thumbnail_url: z.string(),
  creator_username: z.string(),
});

export const campaignAnalysisInsightGroupSchema = z.strictObject({
  label: z.string(),
  name: z.string(),
  description: z.string(),
  num_posts: z.number().int(),
  median_target_performance_metric_ratio_to_population: z.number(),
  representative_post_examples: z.array(
    campaignAnalysisRepresentativePostExampleSchema,
  ),
});

export const campaignAnalysisInsightSchema = z.strictObject({
  heading: z.string(),
  title: z.string(),
  lead: z.string(),
  explanation: z.string(),
  groups_compared: z.array(campaignAnalysisInsightGroupSchema),
  guide: z.string(),
});

export const campaignAnalysisPeriodSchema = z.strictObject({
  start_date: z.iso.datetime({ offset: true }),
  end_date: z.iso.datetime({ offset: true }),
});

export const campaignAnalysisMetaSchema = z.strictObject({
  title: z.string(),
  dataset: campaignAnalysisDatasetSchema,
  analysis_started_at: z.iso.datetime({ offset: true }),
  period: campaignAnalysisPeriodSchema,
});

export const campaignAnalysisParticipationSchema = z.strictObject({
  total_post_count: z.number().int(),
  participating_creator_count: z.number().int(),
});

export const campaignAnalysisPerformanceQuadrantPointSchema = z.strictObject({
  x: z.number().int(),
  y: z.number(),
});

export const campaignAnalysisPerformanceQuadrantSchema = z.strictObject({
  x_baseline_value: z.number(),
  y_baseline_value: z.number().nullable(),
  points: z.array(campaignAnalysisPerformanceQuadrantPointSchema),
});

export const campaignAnalysisOverviewSchema = z.strictObject({
  participation: campaignAnalysisParticipationSchema,
  aggregate: campaignAnalysisAggregateSchema,
  performance_quadrant: campaignAnalysisPerformanceQuadrantSchema,
  creator_composition: campaignAnalysisCreatorCompositionSchema,
});

export const campaignAnalysisPreflightResponseSchema = z.strictObject({
  total: z.number().int(),
  exclude: z.number().int(),
  analyzable: z.number().int(),
  can_run: z.boolean(),
  minimum_required: z.number().int(),
});

export const campaignAnalysisTopPostExampleSchema = z.strictObject({
  rank: z.number().int(),
  title: z.string(),
  content_description: z.string(),
  why_it_worked: z.string(),
  views: z.number().int(),
  engagement_rate: z.number().nullable(),
  post_source_url: z.string(),
  thumbnail_url: z.string(),
  creator_username: z.string(),
  creator_profile: campaignAnalysisCreatorProfileSchema.optional(),
});

export const campaignAnalysisResultSchema = z.strictObject({
  meta: campaignAnalysisMetaSchema,
  appendix: campaignAnalysisAppendixSchema,
  executive_summary: campaignAnalysisExecutiveSummarySchema,
  top_post_examples: z.array(campaignAnalysisTopPostExampleSchema),
  insights: z.array(campaignAnalysisInsightSchema),
  content_creation_guides: z.array(campaignAnalysisContentCreationGuideSchema),
  creator_discovery_guides: z.array(
    campaignAnalysisCreatorDiscoveryGuideSchema,
  ),
  overview: campaignAnalysisOverviewSchema,
});

export const campaignAnalysisStatusResponseSchema = z.strictObject({
  run_id: z.string(),
  status: campaignAnalysisStatusSchema,
  dimensions: z.array(campaignAnalysisDimensionSchema),
  target_metric: campaignAnalysisTargetMetricSchema,
  failure_reason: campaignAnalysisFailureReasonSchema.nullable().optional(),
  post_count: z.number().int().nullable().optional(),
  created_at: z.iso.datetime({ offset: true }),
  started_at: z.iso.datetime({ offset: true }).nullable().optional(),
  completed_at: z.iso.datetime({ offset: true }).nullable().optional(),
});

export const campaignLabelSchema = z.enum(CAMPAIGN_LABEL);

export const campaignCreatorItemResponseSchema = z.looseObject({
  id: z.string(),
  platform: z.string(),
  username: z.string(),
  nickname: z.string().nullable().optional(),
  profile_image_url: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  label: campaignLabelSchema.nullable().optional(),
  post_count: z.number().int(),
  created_at: z.iso.datetime({ offset: true }),
});

export const paginationSchema = z.looseObject({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
});

export const campaignCreatorListResponseSchema = z.looseObject({
  items: z.array(campaignCreatorItemResponseSchema),
  pagination: paginationSchema,
});

export const campaignItemTypeSchema = z.enum(CAMPAIGN_ITEM_TYPE);

export const campaignPlatformSchema = z.enum(CAMPAIGN_PLATFORM);

export const campaignItemResponseSchema = z.looseObject({
  id: z.string(),
  type: campaignItemTypeSchema,
  platform: campaignPlatformSchema.optional(),
  handle: z.string().optional(),
  post_url: z.string().optional(),
  label: campaignLabelSchema.nullable().optional(),
});

export const campaignStatusSchema = z.enum(CAMPAIGN_STATUS);

export const campaignDetailResponseSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  status: campaignStatusSchema,
  start_date: z.iso.datetime({ offset: true }),
  end_date: z.iso.datetime({ offset: true }).nullable().optional(),
  keywords: z.array(z.string()),
  hashtags: z.array(z.string()),
  mentions: z.array(z.string()),
  platforms: z.array(z.string()),
  items: z.array(campaignItemResponseSchema),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

export const campaignItemReqSchema = z.looseObject({
  type: campaignItemTypeSchema,
  platform: campaignPlatformSchema.optional(),
  handle: z.string().optional(),
  post_url: z.string().optional(),
  label: campaignLabelSchema.nullable().optional(),
});

export const campaignListItemResponseSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  status: campaignStatusSchema,
  start_date: z.iso.datetime({ offset: true }),
  end_date: z.iso.datetime({ offset: true }).nullable().optional(),
  keywords: z.array(z.string()),
  hashtags: z.array(z.string()),
  mentions: z.array(z.string()),
  creator_count: z.number().int(),
  profile_image_urls: z.array(z.string()),
  platforms: z.array(z.string()),
  created_at: z.iso.datetime({ offset: true }),
});

export const campaignListResponseSchema = z.looseObject({
  items: z.array(campaignListItemResponseSchema),
  pagination: paginationSchema,
});

export const campaignMetricsResponseSchema = z.looseObject({
  total_views: z.number().int(),
  total_likes: z.number().int(),
  total_comments: z.number().int(),
  total_shared: z.number().int(),
  total_engagements: z.number().int(),
  average_engagement_rate: z.number(),
});

export const campaignNameAvailabilityResponseSchema = z.looseObject({
  available: z.boolean(),
});

export const campaignOverviewSchema = z.looseObject({
  campaign_creators_count: z.number().int(),
  campaign_creators_with_post_count: z.number().int(),
  tracked_posts_count: z.number().int(),
  recent_posts_count: z.number().int(),
});

export const postAuthorResponseSchema = z.looseObject({
  author_id: z.string(),
  username: z.string(),
  nickname: z.string(),
  profile_image_url: z.string(),
  profile_url: z.string(),
  region: z.string(),
  follower_count: z.number().int(),
  following_count: z.number().int(),
  total_like_count: z.number().int(),
});

export const engagementResponseSchema = z.looseObject({
  view: z.number().int(),
  like: z.number().int(),
  comment: z.number().int(),
  shared: z.number().int(),
  rate: z.number(),
});

export const postVideoResponseSchema = z.looseObject({
  id: z.string().optional(),
  poster_url: z.string().optional(),
  url: z.string().nullable().optional(),
});

export const postExtractionResponseSchema = z.looseObject({
  content: z.string(),
  title: z.string().nullable().optional(),
  hashtags: z.array(z.string()),
  location: z.string(),
  sponsored: z.boolean().nullable().optional(),
  engagement: engagementResponseSchema,
  video: postVideoResponseSchema,
});

export const postSourceResponseSchema = z.looseObject({
  id: z.string(),
  platform: z.string(),
  url: z.string(),
});

export const campaignPostResponseSchema = z.looseObject({
  _id: z.string(),
  date: z.number(),
  scraped_at: z.number(),
  updated_at: z.iso.datetime({ offset: true }).nullable().optional(),
  source: postSourceResponseSchema,
  author: postAuthorResponseSchema,
  extraction: postExtractionResponseSchema,
  board_count: z.number().int().optional(),
});

export const campaignPostSourceSchema = z.enum(CAMPAIGN_POST_SOURCE);

export const campaignPostItemResponseSchema = z.looseObject({
  campaign_post_id: z.string(),
  source: campaignPostSourceSchema,
  is_new: z.boolean(),
  label: campaignLabelSchema.nullable().optional(),
  post: campaignPostResponseSchema,
});

export const campaignPostListResponseSchema = z.looseObject({
  items: z.array(campaignPostItemResponseSchema),
  pagination: paginationSchema,
  last_scraped_at: z.iso.datetime({ offset: true }).nullable().optional(),
});

export const campaignPostSortSchema = z.enum(CAMPAIGN_POST_SORT);

export const campaignSortSchema = z.enum(CAMPAIGN_SORT);

export const countResponseSchema = z.looseObject({ count: z.number().int() });

export const createCampaignAnalysisRequestSchema = z.strictObject({
  dimensions: z.array(campaignAnalysisDimensionSchema).min(1).max(5),
  target_metric: campaignAnalysisTargetMetricSchema,
});

export const createCampaignAnalysisResponseSchema = z.strictObject({
  run_id: z.string(),
  status: campaignAnalysisStatusSchema,
});

export const createCampaignRequestSchema = z.looseObject({
  name: z.string(),
  start_date: z.iso.datetime({ offset: true }),
  end_date: z.iso.datetime({ offset: true }).optional(),
  keywords: z.array(z.string()).optional(),
  hashtags: z.array(z.string()).optional(),
  mentions: z.array(z.string()).optional(),
  added_items: z.array(campaignItemReqSchema).optional(),
});

export const errorSchema = z.looseObject({
  code: z.number().int(),
  data: z.unknown().nullable(),
  message: z.string(),
});

export const excludeCampaignPostsRequestSchema = z.looseObject({
  campaign_post_ids: z.array(z.string()).min(1),
});

export const exportTimeseriesRequestSchema = z.looseObject({
  start_date: z.iso.datetime({ offset: true }),
  end_date: z.iso.datetime({ offset: true }),
});

export const updateCampaignAnalysisResultTitleRequestSchema = z.strictObject({
  title: z.string().min(1).max(200),
});

export const updateCampaignNameRequestSchema = z.looseObject({
  name: z.string(),
});

export const updateCampaignRequestSchema = z.looseObject({
  start_date: z.iso.datetime({ offset: true }),
  end_date: z.iso.datetime({ offset: true }).nullable(),
  keywords: z.array(z.string()).optional(),
  hashtags: z.array(z.string()).optional(),
  mentions: z.array(z.string()).optional(),
  items: z.array(campaignItemReqSchema).min(1),
});

export const updateLabelRequestSchema = z.looseObject({
  label: campaignLabelSchema.nullable().optional(),
});
